import React, { useState, useEffect } from "react";
import {
  message, Modal, Table, Tag, Button, Card, Form, Input, InputNumber,
  Select, Switch, Row, Col, Transfer, Space, Grid,
  Dropdown, Menu, Tooltip, Collapse, Divider
} from "antd";
import {
  getCoupons,
  createCoupon,
  updateCoupon,
  deleteCoupon,
  toggleCouponStatus,
  checkCouponValidity,
  getUsers,
  attachUsersToCoupon,
  detachUsersFromCoupon
} from "../../../services/coupons/couponsApi";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
  UserOutlined,
  GlobalOutlined,
  MoreOutlined,
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined
} from "@ant-design/icons";

const { useBreakpoint } = Grid;
const { Option } = Select;
const { TextArea } = Input;
const { Panel } = Collapse;

const CouponsManager = () => {
  const [coupons, setCoupons] = useState([]);
  const [filteredCoupons, setFilteredCoupons] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [usersModalVisible, setUsersModalVisible] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [targetKeys, setTargetKeys] = useState([]);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [filters, setFilters] = useState({});
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const screens = useBreakpoint();

  useEffect(() => {
    loadCoupons();
    loadUsers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [coupons, filters]);

  const loadCoupons = async (params = {}) => {
    setLoading(true);
    try {
      // Construire les paramètres de requête
      const queryParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });

      const response = await getCoupons(queryParams.toString());
      setCoupons(response.data || []);
    } catch (error) {
      message.error("Erreur lors du chargement des coupons");
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await getUsers();
      setUsers(response.data || []);
    } catch (error) {
      message.error("Erreur lors du chargement des utilisateurs");
    } finally {
      setUsersLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...coupons];

    // Appliquer les filtres locaux si nécessaire
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(coupon =>
        coupon.code?.toLowerCase().includes(searchTerm) ||
        coupon.description?.toLowerCase().includes(searchTerm) ||
        coupon.type?.toLowerCase().includes(searchTerm) ||
        (coupon.users && coupon.users.some(user =>
          user.username?.toLowerCase().includes(searchTerm) ||
          user.email?.toLowerCase().includes(searchTerm)
        ))
      );
    }

    setFilteredCoupons(filtered);
  };

  const handleSearch = (values) => {
    setFilters(values);
    // Charger les coupons avec les filtres serveur
    loadCoupons(values);
  };

  const handleResetFilters = () => {
    searchForm.resetFields();
    setFilters({});
    loadCoupons();
  };

  // Format de date sans moment.js
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // Format de date pour les inputs datetime-local
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().slice(0, 16); // Format: YYYY-MM-DDTHH:mm
  };

  const handleCreate = () => {
    setEditingCoupon(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    form.setFieldsValue({
      ...coupon,
      valid_from: coupon.valid_from ? formatDateForInput(coupon.valid_from) : null,
      valid_until: coupon.valid_until ? formatDateForInput(coupon.valid_until) : null
    });
    setModalVisible(true);
  };

  const handleManageUsers = (coupon) => {
    setSelectedCoupon(coupon);
    const assignedUserIds = coupon.users ? coupon.users.map(user => user.id.toString()) : [];
    setTargetKeys(assignedUserIds);
    setUsersModalVisible(true);
  };

  const handleUsersTransfer = async () => {
    try {
      await attachUsersToCoupon(selectedCoupon.id, targetKeys);
      message.success("Utilisateurs mis à jour avec succès");
      setUsersModalVisible(false);
      loadCoupons();
    } catch (error) {
      message.error("Erreur lors de la mise à jour des utilisateurs");
    }
  };

  const handleDelete = async (coupon) => {
    Modal.confirm({
      title: "Confirmer la suppression",
      content: `Êtes-vous sûr de vouloir supprimer le coupon "${coupon.code}" ?`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          await deleteCoupon(coupon.id);
          message.success("Coupon supprimé avec succès");
          loadCoupons();
        } catch (error) {
          message.error("Erreur lors de la suppression");
        }
      }
    });
  };

  const handleToggleStatus = async (coupon) => {
    try {
      await toggleCouponStatus(coupon.id);
      message.success(`Coupon ${coupon.is_active ? "désactivé" : "activé"} avec succès`);
      loadCoupons();
    } catch (error) {
      message.error("Erreur lors du changement de statut");
    }
  };

  const handleSubmit = async (values) => {
    try {
      const formattedValues = {
        ...values,
        valid_from: values.valid_from ? new Date(values.valid_from).toISOString() : null,
        valid_until: values.valid_until ? new Date(values.valid_until).toISOString() : null
      };

      if (editingCoupon) {
        await updateCoupon(editingCoupon.id, formattedValues);
        message.success("Coupon mis à jour avec succès");
      } else {
        await createCoupon(formattedValues);
        message.success("Coupon créé avec succès");
      }

      setModalVisible(false);
      loadCoupons();
    } catch (error) {
      message.error(error.response?.data?.message || "Erreur lors de l'opération");
    }
  };

  const getActionMenu = (record) => (
    <Menu
      items={[
        {
          key: 'edit',
          label: 'Modifier',
          icon: <EditOutlined />,
          onClick: () => handleEdit(record)
        },
        ...(!record.is_global ? [{
          key: 'users',
          label: 'Utilisateurs',
          icon: <UserOutlined />,
          onClick: () => handleManageUsers(record)
        }] : []),
        {
          key: 'toggle',
          label: record.is_active ? 'Désactiver' : 'Activer',
          icon: record.is_active ? <CloseOutlined /> : <CheckOutlined />,
          onClick: () => handleToggleStatus(record)
        },
        {
          key: 'delete',
          label: 'Supprimer',
          icon: <DeleteOutlined />,
          danger: true,
          onClick: () => handleDelete(record)
        }
      ]}
    />
  );

  const columns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      render: (code) => <strong style={{ color: "var(--primary-color)" }}>{code}</strong>,
      responsive: ['xs', 'sm', 'md']
    },
    {
      title: "Portée",
      dataIndex: "is_global",
      key: "is_global",
      render: (isGlobal, record) => (
        <Tag color={isGlobal ? "blue" : "orange"} icon={isGlobal ? <GlobalOutlined /> : <UserOutlined />}>
          {isGlobal ? "Global" : `Spécifique (${record.users?.length || 0})`}
        </Tag>
      ),
      responsive: ['md', 'lg', 'xl']
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (description) => (
        <span style={{ color: "var(--primary-color)" }}>
          {description}
        </span>
      ),
      responsive: ['lg', 'xl']
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => (
        <Tag color={type === "percentage" ? "blue" : "green"}>
          {type === "percentage" ? "%" : "€"}
        </Tag>
      ),
      responsive: ['sm', 'md', 'lg', 'xl']
    },
    {
      title: "Valeur",
      dataIndex: "value",
      key: "value",
      render: (value, record) => (
        <span style={{ color: "var(--primary-color)" }}>
          {record.type === "percentage" ? `${value}%` : `$${value}`}
        </span>
      ),
      responsive: ['sm', 'md', 'lg', 'xl']
    },
    {
      title: "Utilisations",
      dataIndex: "used_count",
      key: "used_count",
      render: (used, record) => (
        <span style={{ color: "var(--primary-color)" }}>
          {used} / {record.usage_limit || "∞"}
        </span>
      ),
      responsive: ['md', 'lg', 'xl']
    },
    {
      title: "Validité",
      key: "validity",
      render: (_, record) => (
        <div style={{
          fontSize: screens.xs ? '12px' : '14px',
          color: "var(--primary-color)"
        }}>
          <div>Du: {formatDate(record.valid_from)}</div>
          <div>Au: {formatDate(record.valid_until)}</div>
        </div>
      ),
      responsive: ['lg', 'xl']
    },
    {
      title: "Statut",
      dataIndex: "is_active",
      key: "is_active",
      render: (isActive) => (
        <Tag color={isActive ? "green" : "red"}>
          {isActive ? "Actif" : "Inactif"}
        </Tag>
      ),
      responsive: ['sm', 'md', 'lg', 'xl']
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        screens.xs ? (
          <Dropdown overlay={getActionMenu(record)} trigger={['click']}>
            <Button
              icon={<MoreOutlined />}
              size="small"
              style={{ color: "var(--primary-color)" }}
            />
          </Dropdown>
        ) : (
          <Space size="small" wrap>
            <Tooltip title="Modifier">
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => handleEdit(record)}
                style={{ color: "var(--primary-color)" }}
              />
            </Tooltip>

            {!record.is_global && (
              <Tooltip title="Gérer les utilisateurs">
                <Button
                  icon={<UserOutlined />}
                  size="small"
                  onClick={() => handleManageUsers(record)}
                  style={{ color: "var(--primary-color)" }}
                />
              </Tooltip>
            )}

            <Tooltip title={record.is_active ? "Désactiver" : "Activer"}>
              <Button
                icon={record.is_active ? <CloseOutlined /> : <CheckOutlined />}
                size="small"
                onClick={() => handleToggleStatus(record)}
                type={record.is_active ? "default" : "primary"}
                style={record.is_active ? {} : {
                  backgroundColor: "var(--primary-color3)",
                  borderColor: "var(--primary-color3)"
                }}
              />
            </Tooltip>

            <Tooltip title="Supprimer">
              <Button
                icon={<DeleteOutlined />}
                size="small"
                danger
                onClick={() => handleDelete(record)}
              />
            </Tooltip>
          </Space>
        )
      )
    }
  ];

  return (
    <div style={{
      padding: '24px',
      backgroundColor: "var(--background-body)",
      minHeight: '100vh',
      color: "var(--primary-color)"
    }}>
      <Card
        style={{
          backgroundColor: "var(--background-body)",
          border: "1px solid var(--primary-color6)",
        }}
        bodyStyle={{ padding: screens.xs ? '8px' : '16px' }}
        title={
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: 'wrap',
            gap: '8px'
          }}>
            <h3 style={{
              color: "var(--primary-color3)",
              margin: 0,
              fontSize: screens.xs ? '18px' : '24px'
            }}>
              Gestion des Coupons
            </h3>
            <Space>
              <Button
                icon={<FilterOutlined />}
                onClick={() => setFiltersVisible(!filtersVisible)}
                size={screens.xs ? "small" : "middle"}
                style={{ color: "var(--primary-color)" }}
              >
                Filtres
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
                size={screens.xs ? "small" : "middle"}
                style={{
                  backgroundColor: "var(--primary-color3)",
                  borderColor: "var(--primary-color3)"
                }}
              >
                Nouveau coupon
              </Button>
            </Space>
          </div>
        }
      >
        {/* Filtres de recherche */}
        <Collapse activeKey={filtersVisible ? ['filters'] : []} onChange={() => setFiltersVisible(!filtersVisible)}>
          <Panel header="Filtres de recherche" key="filters">
            <Form
              form={searchForm}
              layout="vertical"
              onFinish={handleSearch}
              initialValues={{
                is_active: '',
                is_global: '',
                type: ''
              }}
            >
              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item label="Recherche globale" name="search">
                    <Input
                      placeholder="Code, description, utilisateur..."
                      prefix={<SearchOutlined />}
                      size={screens.xs ? "small" : "middle"}
                      style={{
                        backgroundColor: "var(--background-body)",
                        color: "var(--primary-color)",
                        border: "1px solid var(--primary-color6)"
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={4}>
                  <Form.Item label="Type" name="type">
                    <Select
                      placeholder="Tous"
                      size={screens.xs ? "small" : "middle"}
                      style={{
                        backgroundColor: "var(--background-body)",
                        color: "var(--primary-color)"
                      }}
                    >
                      <Option value="">Tous</Option>
                      <Option value="percentage">Pourcentage</Option>
                      <Option value="fixed">Fixe</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={4}>
                  <Form.Item label="Statut" name="is_active">
                    <Select
                      placeholder="Tous"
                      size={screens.xs ? "small" : "middle"}
                      style={{
                        backgroundColor: "var(--background-body)",
                        color: "var(--primary-color)"
                      }}
                    >
                      <Option value="">Tous</Option>
                      <Option value="true">Actif</Option>
                      <Option value="false">Inactif</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={4}>
                  <Form.Item label="Portée" name="is_global">
                    <Select
                      placeholder="Tous"
                      size={screens.xs ? "small" : "middle"}
                      style={{
                        backgroundColor: "var(--background-body)",
                        color: "var(--primary-color)"
                      }}
                    >
                      <Option value="">Tous</Option>
                      <Option value="true">Global</Option>
                      <Option value="false">Spécifique</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={4}>
                  <Form.Item label=" " colon={false}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Button
                        type="primary"
                        htmlType="submit"
                        icon={<SearchOutlined />}
                        size={screens.xs ? "small" : "middle"}
                        style={{
                          backgroundColor: "var(--primary-color3)",
                          borderColor: "var(--primary-color3)"
                        }}
                      >
                        Rechercher
                      </Button>
                      <Button
                        onClick={handleResetFilters}
                        size={screens.xs ? "small" : "middle"}
                        style={{ color: "var(--primary-color)" }}
                      >
                        Réinitialiser
                      </Button>
                    </Space>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Panel>
        </Collapse>

        <Divider />

        <Table
          columns={columns}
          dataSource={filteredCoupons}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} sur ${total} coupons`,
            responsive: true
          }}
          scroll={{ x: true }}
          size={screens.xs ? "small" : "middle"}
          style={{
            backgroundColor: "var(--background-body)",
          }}
        />
      </Card>

      {/* Modal pour créer/modifier un coupon */}
      <Modal
        title={editingCoupon ? "Modifier le coupon" : "Nouveau coupon"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={screens.xs ? "95%" : 700}
        style={{ top: screens.xs ? 20 : 50 }}
        styles={{
          body: {
            backgroundColor: "var(--background-body)",
            color: "var(--primary-color)"
          }
        }}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Code"
                name="code"
                rules={[{ required: true, message: 'Veuillez saisir un code' }]}
              >
                <Input
                  placeholder="Code du coupon"
                  style={{
                    backgroundColor: "var(--background-body)",
                    color: "var(--primary-color)",
                    border: "1px solid var(--primary-color6)"
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Type"
                name="type"
                rules={[{ required: true, message: 'Veuillez sélectionner un type' }]}
              >
                <Select
                  placeholder="Sélectionnez le type"
                  getPopupContainer={trigger => trigger.parentNode}
                  dropdownStyle={{
                    backgroundColor: "var(--background-body)",
                    color: "var(--primary-color)"
                  }}
                >
                  <Option value="percentage">Pourcentage</Option>
                  <Option value="fixed">Montant fixe</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Valeur"
            name="value"
            rules={[{ required: true, message: 'Veuillez saisir une valeur' }]}
          >
            <InputNumber
              style={{
                width: '100%',
                backgroundColor: "var(--background-body)",
                color: "var(--primary-color)"
              }}
              min={0}
              placeholder="Valeur du coupon"
            />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
          >
            <TextArea
              rows={3}
              placeholder="Description du coupon"
              style={{
                backgroundColor: "var(--background-body)",
                color: "var(--primary-color)",
                border: "1px solid var(--primary-color6)"
              }}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Date de début"
                name="valid_from"
                getValueFromEvent={(e) => e.target.value}
              >
                <input
                  type="datetime-local"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid var(--primary-color6)',
                    borderRadius: '6px',
                    backgroundColor: "var(--background-body)",
                    color: "var(--primary-color)",
                    colorScheme: "dark",
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Date de fin"
                name="valid_until"
                getValueFromEvent={(e) => e.target.value}
              >
                <input
                  type="datetime-local"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid var(--primary-color6)',
                    borderRadius: '6px',
                    backgroundColor: "var(--background-body)",
                    color: "var(--primary-color)",
                    colorScheme: "dark",
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Limite d'utilisation"
                name="usage_limit"
              >
                <InputNumber
                  style={{
                    width: '100%',
                    backgroundColor: "var(--background-body)",
                    color: "var(--primary-color)"
                  }}
                  min={0}
                  placeholder="Nombre maximum d'utilisations"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Portée"
                name="is_global"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Global"
                  unCheckedChildren="Spécifique"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Statut"
            name="is_active"
            valuePropName="checked"
          >
            <Switch
              checkedChildren="Actif"
              unCheckedChildren="Inactif"
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              style={{
                marginRight: 8,
                backgroundColor: "var(--primary-color3)",
                borderColor: "var(--primary-color3)"
              }}
            >
              {editingCoupon ? "Modifier" : "Créer"}
            </Button>
            <Button onClick={() => setModalVisible(false)}>
              Annuler
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal pour gérer les utilisateurs */}
      <Modal
        title={`Gérer les utilisateurs - ${selectedCoupon?.code}`}
        open={usersModalVisible}
        onCancel={() => setUsersModalVisible(false)}
        onOk={handleUsersTransfer}
        width={screens.xs ? "95%" : 800}
        style={{ top: screens.xs ? 20 : 50 }}
        okText="Enregistrer"
        cancelText="Annuler"
        styles={{
          body: {
            backgroundColor: "var(--background-body)",
            color: "var(--primary-color)"
          }
        }}
      >
        <Transfer
          dataSource={users.map(user => ({
            key: user.id.toString(),
            title: user.name || user.email,
            description: user.email
          }))}
          targetKeys={targetKeys}
          onChange={setTargetKeys}
          render={item => `${item.title} (${item.description})`}
          listStyle={{
            width: screens.xs ? '45%' : '300px',
            height: screens.xs ? '300px' : '400px'
          }}
          titles={['Disponibles', 'Assignés']}
          showSearch
          operations={['Ajouter', 'Retirer']}
        />
      </Modal>
    </div>
  );
};

export default CouponsManager;